import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import "./GridHeader.scss";

const NO_SORTING = "none";

const GridHeader = (props) => {
  const { displayName, enableSorting, setSort, column, renderExtaElement } =
    props;
  const [sorting, setSorting] = useState(NO_SORTING);
  const [enableTooltip, setEnableTooltip] = useState(true);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const textRef = useRef(null);
  const tooltipRef = useRef(null);
  const delayInMs = 200;

  const checkOverflow = () => {
    const textElement = textRef.current;
    if (textElement) {
      const enableTooltip = textElement.scrollWidth > textElement.clientWidth;
      setEnableTooltip(enableTooltip);
    }
  };

  useEffect(() => {
    column.addEventListener("sortChanged", () => {
      setSorting(NO_SORTING);
      setTimeout(() => {
        checkOverflow();
      }, delayInMs);
    });
  }, [column]);

  useEffect(() => {
    setTimeout(() => {
      checkOverflow();
    }, delayInMs);

    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);

  const handleSort = () => {
    let newSorting;
    if (sorting === NO_SORTING) {
      newSorting = "asc";
    } else if (sorting === "asc") {
      newSorting = "desc";
    } else {
      newSorting = NO_SORTING;
    }

    setSort(newSorting, false);
    setSorting(newSorting);
  };

  const calculateTooltipPosition = (childElement) => {
    const tooltipElement = tooltipRef.current;

    if (childElement && tooltipElement) {
      const { top, left, height } = childElement.getBoundingClientRect();
      const tooltipTop = top - tooltipElement.offsetHeight - height / 2;
      const tooltipLeft = left;

      return {
        top: `${tooltipTop}px`,
        left: `${tooltipLeft}px`,
        display: "inline-block",
      };
    }

    return { top: 0, left: 0 };
  };

  return (
    <div className="ag-header-cell-comp-wrapper" onClick={handleSort}>
      <div
        className={classNames("ag-cell-label-container", {
          [`ag-header-cell-sorted-${sorting}`]: sorting !== undefined,
        })}
      >
        <div className="ag-header-cell-label" role="presentation">
          <span
            className="ag-header-cell-text time-meeting-header"
            ref={textRef}
          >
            {renderExtaElement && renderExtaElement()}
            <span
              onMouseEnter={(e) => {
                if (enableTooltip) {
                  const position = calculateTooltipPosition(
                    e.currentTarget.parentElement.parentElement.parentElement
                  );
                  setTooltipPosition(position);
                }
              }}
              onMouseLeave={() => {
                if (enableTooltip) {
                  setTooltipPosition(null);
                }
              }}
            >
              {displayName}
            </span>
          </span>
          {enableSorting && (
            <span className="ag-sort-indicator-container">
              <span
                className="ag-sort-indicator-icon ag-sort-order ag-hidden"
                aria-hidden="true"
              ></span>
              <span
                className={classNames(
                  "ag-sort-indicator-icon",
                  "ag-sort-ascending-icon",
                  { "ag-hidden": sorting !== "asc" }
                )}
                aria-hidden="true"
              >
                <span
                  className="ag-icon ag-icon-asc"
                  role="presentation"
                ></span>
              </span>
              <span
                className={classNames(
                  "ag-sort-indicator-icon",
                  "ag-sort-descending-icon",
                  { "ag-hidden": sorting !== "desc" }
                )}
                aria-hidden="true"
              >
                <span
                  className="ag-icon ag-icon-desc"
                  role="presentation"
                ></span>
              </span>
            </span>
          )}
        </div>
      </div>
      {enableTooltip &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            style={tooltipPosition}
            className="grid-header-tooltip"
            role="dialog"
          >
            {displayName}
          </div>,
          document.body
        )}
    </div>
  );
};

GridHeader.propTypes = {
  displayName: PropTypes.string,
  enableSorting: PropTypes.bool,
  column: PropTypes.object,
  setSort: PropTypes.func,
  api: PropTypes.any,
  renderExtaElement: PropTypes.func,
};

export default GridHeader;
